import type { ReactNode } from 'react';
import { TbAlertTriangleFilled, TbCircleCheckFilled, TbCircleXFilled, TbInfoCircleFilled } from 'react-icons/tb';

import type { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export type PlatformStatus = 'default' | 'error' | 'info' | 'success' | 'warning';

type PlatformToken = ReturnType<typeof useAntDesignTheme>['token'];

export interface PlatformStatusPalette {
    accent: string;
    background: string;
    border: string;
    description: string;
    iconBackground: string;
    pattern: string;
    title: string;
}

export const PLATFORM_STATUS_ICONS: Record<PlatformStatus, ReactNode> = {
    default: <TbInfoCircleFilled />,
    error: <TbCircleXFilled />,
    info: <TbInfoCircleFilled />,
    success: <TbCircleCheckFilled />,
    warning: <TbAlertTriangleFilled />,
};

const createStatusDescriptionColor = (statusColor: string, secondaryColor: string) =>
    `color-mix(in srgb, ${statusColor} 45%, ${secondaryColor})`;

/**
 * Single token-derived status contract for alerts, action feedback, and live notifications.
 * Keeping this outside either renderer prevents visually equivalent states from drifting.
 */
export const createPlatformStatusPalettes = (token: PlatformToken): Record<PlatformStatus, PlatformStatusPalette> => ({
    default: {
        accent: token.colorTextTertiary,
        background: token.colorBgBase,
        border: token.colorBorder,
        description: token.colorTextSecondary,
        iconBackground: token.colorFillQuaternary,
        pattern: token.colorTextQuaternary,
        title: token.colorText,
    },
    error: {
        accent: token.colorError,
        background: token.colorErrorBg,
        border: token.colorErrorBorder,
        description: createStatusDescriptionColor(token.colorErrorText, token.colorTextSecondary),
        iconBackground: token.colorErrorBgHover,
        pattern: token.colorErrorBorder,
        title: token.colorErrorText,
    },
    info: {
        accent: token.colorInfo,
        background: token.colorInfoBg,
        border: token.colorInfoBorder,
        description: createStatusDescriptionColor(token.colorInfoText, token.colorTextSecondary),
        iconBackground: token.colorInfoBgHover,
        pattern: token.colorInfoBorder,
        title: token.colorInfoText,
    },
    success: {
        accent: token.colorSuccess,
        background: token.colorSuccessBg,
        border: token.colorSuccessBorder,
        description: createStatusDescriptionColor(token.colorSuccessText, token.colorTextSecondary),
        iconBackground: token.colorSuccessBgHover,
        pattern: token.colorSuccessBorder,
        title: token.colorSuccessText,
    },
    warning: {
        accent: token.colorWarning,
        background: token.colorWarningBg,
        border: token.colorWarningBorder,
        description: createStatusDescriptionColor(token.colorWarningText, token.colorTextSecondary),
        iconBackground: token.colorWarningBgHover,
        pattern: token.colorWarningBorder,
        title: token.colorWarningText,
    },
});
